import React, { useEffect, useId, useRef, useState } from "react";
import { ACCENT } from "./shared";

// A styled drop-down list for Artist Studio forms (the browser's own list looks different on every system).
// Controlled: `value` is the selected option's value, `onChange(value)` is called when another one is chosen.
// Keyboard: Enter / Space / Arrow keys open it, Arrow keys, Home and End move, Enter chooses, Escape and Tab close it.
export default function Dropdown({ value, options, onChange, ariaLabel, id, disabled = false, hasError = false }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // Keeps the highlighted option in view when the list is long.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const openList = () => { setActive(selectedIndex); setOpen(true); };
  const choose = (i) => {
    const option = options[i];
    setOpen(false);
    if (option && option.value !== value) onChange(option.value);
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(options.length - 1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(active); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button" id={id} disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
        onClick={() => (open ? setOpen(false) : openList())} onKeyDown={onKeyDown}
        className={`w-full rounded-xl border bg-white px-4 py-2.5 text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 disabled:opacity-60 ${hasError ? "border-red-400" : "border-gray-200"}`}
        style={{ "--tw-ring-color": ACCENT }} data-testid="dropdown-button"
      >
        <span className="truncate">{selected ? selected.label : ""}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul
          ref={listRef} id={listId} role="listbox" aria-label={ariaLabel} tabIndex={-1}
          className="absolute left-0 right-0 z-30 mt-1.5 max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white py-1.5 shadow-lg"
          data-testid="dropdown-list"
        >
          {options.map((o, i) => (
            <li
              key={o.value} role="option" aria-selected={o.value === value}
              onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(i)}
              className={`flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-[15px] ${i === active ? "bg-[#eef5f0]" : ""} ${o.value === value ? "font-semibold text-gray-900" : "text-gray-700"}`}
              data-testid="dropdown-option"
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
